import { useCallback, useRef, useState } from 'react';
import { api } from './api.js';
import { useT, type Key } from './i18n.js';
import type { Payload } from './payload.js';

/**
 * 1:1-Anrufe über WebRTC.
 *
 * Die Medien selbst sind durch DTLS-SRTP verschlüsselt — das macht der Browser,
 * es ist Teil von WebRTC und nicht abschaltbar. Die Signalisierung (SDP und
 * ICE-Kandidaten) läuft durch denselben Signal-Kanal wie Chatnachrichten und
 * ist damit ebenfalls Ende-zu-Ende verschlüsselt.
 *
 * Offen bleibt die Bindung der DTLS-Fingerprints an die Signal-Identität. Weil
 * das SDP schon E2E-verschlüsselt übertragen wird, kann ein bösartiger Server
 * es nicht austauschen — er sieht es gar nicht. Ein Angreifer, der stattdessen
 * ein zusätzliches Gerät einschleust, würde über die Sicherheitsnummer sichtbar.
 */

/**
 * Bewusst LEER — kein öffentlicher STUN-Server.
 *
 * Der naheliegende Eintrag wäre `stun:stun.l.google.com:19302`. Er würde
 * funktionieren, aber bei jedem Anruf Googles Server mitteilen, dass dieser
 * Nutzer gerade telefoniert. Für einen Messenger, der Metadaten gerade
 * vermeiden will, ist das der falsche Standardwert.
 *
 * Ohne STUN entstehen nur Host-Kandidaten. Das reicht für Geräte im selben
 * Netz — zwei Browserfenster auf demselben Rechner, ein gemeinsames WLAN.
 * Über Netzgrenzen hinweg braucht es eigenes STUN/TURN; die Zugangsdaten
 * kommen deshalb zur Laufzeit vom eigenen Server (/v1/turn) und stehen
 * bewusst nicht im Build: Sie sind kurzlebig und dürfen nicht in einem Asset
 * festbacken, das ein Jahr lang gecacht wird.
 *
 * Sobald TURN verfügbar ist, gilt `iceTransportPolicy: 'relay'`: Bei direkter
 * Verbindung lernen beide Seiten die IP-Adresse der anderen — ein einziger
 * Anruf verrät den Standort. Der Preis von Relay ist Bandbreite. Ohne TURN
 * bleibt es bei der Vorgabe 'all', sonst käme gar keine Verbindung zustande.
 * Siehe docs/01-architektur.md §5.
 */

export type CallState = 'idle' | 'calling' | 'ringing' | 'connecting' | 'connected' | 'ended';

export type CallInfo = {
  state: CallState;
  peerDeviceId: string | null;
  peerName: string;
  video: boolean;
  incoming: boolean;
  error: string | null;
  /** Kamera vom Nutzer abgeschaltet — das Gespraech laeuft weiter. */
  cameraOff: boolean;
  /** Das Anruffenster ist eingeklappt, der Anruf laeuft im Hintergrund. */
  minimised: boolean;
};

const IDLE: CallInfo = {
  state: 'idle',
  peerDeviceId: null,
  peerName: '',
  video: false,
  incoming: false,
  error: null,
  cameraOff: false,
  minimised: false,
};

type SendSignal = (toDeviceId: string, payload: Payload) => Promise<void>;

export function useCall(sendSignal: SendSignal, token: string) {
  const t = useT();
  const [info, setInfo] = useState<CallInfo>(IDLE);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  // Einmal geholt und bis zum Ablauf behalten: Jeder Anruf ein neuer Abruf
  // wäre ein zusätzliches Signal an den Server, wann jemand telefoniert.
  const iceRef = useRef<{ servers: RTCIceServer[]; expiresAt: number } | null>(null);

  const iceServers = useCallback(async (): Promise<RTCIceServer[]> => {
    const cached = iceRef.current;
    if (cached && Date.now() < cached.expiresAt - 60_000) return cached.servers;

    try {
      const res = await api.turnCredentials(token);
      iceRef.current = { servers: res.iceServers, expiresAt: res.expiresAt };
      return res.iceServers;
    } catch {
      // Kein TURN erreichbar ist kein Grund, den Anruf gar nicht erst zu
      // versuchen — im selben Netz klappt er auch ohne.
      return [];
    }
  }, [token]);

  // Bei einem ausgehenden Anruf klingeln ALLE Geräte der Gegenstelle. Wer
  // zuerst annimmt, bekommt das Gespräch; die übrigen werden abgebrochen.
  // Ein Anruf an nur ein Gerät ist eine Wette darauf, dass die Person gerade
  // an diesem sitzt — bei mehreren Geräten klingelt es sonst ins Leere.
  // Welche Kamera gerade läuft. 'user' ist vorn, 'environment' hinten.
  const facingRef = useRef<'user' | 'environment'>('user');

  const ringingRef = useRef<string[]>([]);

  // Der Signalhandler braucht den aktuellen Zustand, darf aber nicht bei jeder
  // Zustandsaenderung neu gebaut werden — sonst gingen Signale verloren.
  const infoRef = useRef<CallInfo>(IDLE);
  infoRef.current = info;

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const callIdRef = useRef<string | null>(null);
  const peerRef = useRef<string | null>(null);
  const localRef = useRef<MediaStream | null>(null);
  // Kandidaten, die vor dem Setzen der Remote-Description eintreffen. Ohne
  // Zwischenspeicher wirft addIceCandidate, und die Verbindung kommt nie
  // zustande — ein klassischer WebRTC-Stolperstein.
  const pendingIce = useRef<RTCIceCandidateInit[]>([]);

  const cleanUp = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    localRef.current?.getTracks().forEach((track) => track.stop());
    localRef.current = null;
    callIdRef.current = null;
    peerRef.current = null;
    ringingRef.current = [];
    // Nächster Anruf beginnt wieder mit der Frontkamera.
    facingRef.current = 'user';
    pendingIce.current = [];
    setLocalStream(null);
    setRemoteStream(null);
  }, []);

  /**
   * Gespräch beenden, Grund anzeigen, danach zurück auf 'idle'.
   *
   * Der Rückweg ist der eigentliche Punkt: Die Anruf-Knöpfe sind an
   * `state !== 'idle'` gekoppelt. Wer 'ended' setzt und dort stehen bleibt,
   * sperrt die Knöpfe dauerhaft — dann hilft nur noch ein Neuladen der Seite.
   */
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const endWith = useCallback(
    (error: string | null) => {
      cleanUp();
      setInfo((prev) => ({ ...IDLE, state: 'ended', peerName: prev.peerName, error }));
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
      // Frueher 2500 ms. Das fuehlte sich an, als haenge das Fenster: Wer
      // aufgelegt hat, will zurueck in den Chat, nicht auf eine Meldung
      // warten. Eine Sekunde reicht, um den Grund zu lesen.
      endTimerRef.current = setTimeout(() => setInfo(IDLE), 1000);
    },
    [cleanUp],
  );

  const buildConnection = useCallback(
    async (callId: string, video: boolean) => {
      const servers = await iceServers();

      // Relay erzwingen, sobald TURN da ist: Sonst wählt der Browser bei
      // Gelegenheit den direkten Weg, und beide Seiten sehen die IP der
      // anderen. Ohne TURN bliebe bei 'relay' gar keine Verbindung übrig.
      const pc = new RTCPeerConnection({
        iceServers: servers,
        ...(servers.length > 0 ? { iceTransportPolicy: 'relay' as const } : {}),
      });

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        // Solange niemand angenommen hat, gehen Kandidaten an alle
        // klingelnden Geräte — welches antwortet, steht noch nicht fest.
        const targets = peerRef.current ? [peerRef.current] : ringingRef.current;
        for (const target of targets) {
          void sendSignal(target, {
            kind: 'call',
            callId,
            signal: 'ice',
            video,
            candidate: event.candidate.toJSON(),
          });
        }
      };

      pc.ontrack = (event) => {
        setRemoteStream(event.streams[0] ?? null);
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          setInfo((prev) => ({ ...prev, state: 'connected' }));
        } else if (pc.connectionState === 'failed') {
          endWith(t('callConnectionFailed'));
        }
      };

      pcRef.current = pc;
      return pc;
    },
    [iceServers, sendSignal, endWith],
  );

  async function captureLocal(video: boolean): Promise<MediaStream> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      // Beim Start die Frontkamera: Bei einem Videoanruf will man sich zeigen,
      // nicht filmen, was vor einem liegt. 'user' ist am Notebook ohnehin die
      // einzige Kamera und dort wirkungslos.
      video: video ? { facingMode: facingRef.current } : false,
    });
    localRef.current = stream;
    setLocalStream(stream);
    return stream;
  }

  /**
   * Zwischen Front- und Rückkamera wechseln.
   *
   * Der Stream wird nicht neu ausgehandelt: `replaceTrack` tauscht die Spur im
   * bestehenden Sender aus. Ein neues Angebot mitten im Gespräch würde den
   * Anruf für einen Moment einfrieren — und bei manchen Gegenstellen ganz
   * abbrechen.
   */
  const switchCamera = useCallback(async () => {
    const pc = pcRef.current;
    const old = localRef.current;
    if (!pc || !old) return;

    const next = facingRef.current === 'user' ? 'environment' : 'user';

    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: next },
      });
      const track = fresh.getVideoTracks()[0];
      if (!track) {
        fresh.getTracks().forEach((t) => t.stop());
        return;
      }

      const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(track);

      // Die alte Videospur freigeben, sonst bleibt die Kamera belegt und die
      // Aufnahmeleuchte an. Die Tonspur bleibt unangetastet.
      for (const t of old.getVideoTracks()) {
        old.removeTrack(t);
        t.stop();
      }
      old.addTrack(track);

      facingRef.current = next;
      // Neues MediaStream-Objekt, damit React das Vorschaubild neu anhängt.
      const updated = new MediaStream(old.getTracks());
      localRef.current = updated;
      setLocalStream(updated);
    } catch {
      // Gerät ohne zweite Kamera oder verweigerte Freigabe: Der Anruf läuft
      // mit der bisherigen Kamera weiter, das ist kein Abbruchgrund.
    }
  }, []);

  /* ── Anruf starten ────────────────────────────────────────────────────── */

  const start = useCallback(
    async (peerDeviceIds: string[], peerName: string, video: boolean) => {
      if (peerDeviceIds.length === 0) return;

      // Ein noch laufender Rücksetz-Timer vom letzten Gespräch würde diesen
      // Anruf mitten im Klingeln auf 'idle' zurückwerfen.
      if (endTimerRef.current) {
        clearTimeout(endTimerRef.current);
        endTimerRef.current = null;
      }

      const callId = crypto.randomUUID();
      callIdRef.current = callId;
      // Noch offen, welches Gerät annimmt.
      peerRef.current = null;
      ringingRef.current = [...peerDeviceIds];

      setInfo({
        state: 'calling',
        peerDeviceId: null,
        peerName,
        video,
        incoming: false,
        error: null,
        cameraOff: false,
        minimised: false,
      });

      try {
        const stream = await captureLocal(video);
        const pc = await buildConnection(callId, video);
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        // Dasselbe Angebot an alle Geräte. Nur eine Antwort wird angenommen;
        // die Verbindung entsteht mit dem Gerät, das zuerst zusagt.
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        await Promise.all(
          peerDeviceIds.map((deviceId) =>
            sendSignal(deviceId, {
              kind: 'call',
              callId,
              signal: 'offer',
              video,
              sdp: offer.sdp,
            }),
          ),
        );
      } catch (err) {
        endWith(describeMediaError(err, t));
      }
    },
    [buildConnection, sendSignal, endWith],
  );

  /* ── Eingehenden Anruf annehmen ───────────────────────────────────────── */

  const accept = useCallback(async () => {
    const pc = pcRef.current;
    const peerDeviceId = peerRef.current;
    const callId = callIdRef.current;
    if (!pc || !peerDeviceId || !callId) return;

    setInfo((prev) => ({ ...prev, state: 'connecting' }));

    try {
      const stream = await captureLocal(info.video);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      await sendSignal(peerDeviceId, {
        kind: 'call',
        callId,
        signal: 'answer',
        video: info.video,
        sdp: answer.sdp,
      });
    } catch (err) {
      endWith(describeMediaError(err, t));
    }
  }, [info.video, sendSignal, endWith]);

  /* ── Beenden und Ablehnen ─────────────────────────────────────────────── */

  const hangUp = useCallback(
    async (signal: 'hangup' | 'reject' = 'hangup') => {
      const callId = callIdRef.current;
      // Auflegen erreicht ALLE Geräte, die noch klingeln — sonst läutet das
      // Telefon der Gegenstelle weiter, obwohl der Anrufer aufgelegt hat.
      const targets = peerRef.current ? [peerRef.current] : ringingRef.current;

      if (callId && targets.length > 0) {
        await Promise.all(
          targets.map((target) =>
            sendSignal(target, {
              kind: 'call',
              callId,
              signal,
              video: false,
            }).catch(() => {
              // Die Gegenseite ist vielleicht schon weg — das Auflegen darf
              // daran nicht scheitern.
            }),
          ),
        );
      }

      cleanUp();
      setInfo(IDLE);
    },
    [sendSignal, cleanUp],
  );

  /* ── Eingehende Signale ───────────────────────────────────────────────── */

  const handleSignal = useCallback(
    async (fromDeviceId: string, peerName: string, payload: Payload) => {
      if (payload.kind !== 'call') return;

      switch (payload.signal) {
        case 'offer': {
          /*
           * Beide rufen gleichzeitig an ("glare").
           *
           * Vorher meldeten sich beide Seiten gegenseitig besetzt und niemand
           * kam durch — der Fall ist haeufiger, als er klingt: Zwei Leute, die
           * sich verabredet haben, tippen im selben Moment auf den Hoerer.
           *
           * Aufgeloest wird es ohne Absprache: Beide Seiten kennen beide
           * Anruf-Kennungen und vergleichen sie. Die kleinere gewinnt. Weil
           * beide dieselbe Regel auf dieselben Werte anwenden, kommen sie
           * zwangslaeufig zum gleichen Ergebnis — einer wird zum Anrufer, der
           * andere zum Angerufenen.
           */
          const ownCallId = callIdRef.current;
          const outgoing = ownCallId !== null && !infoRef.current.incoming;

          if (outgoing && ownCallId !== null) {
            if (ownCallId < payload.callId) {
              // Der eigene Anruf gewinnt: Das fremde Angebot wird abgelehnt,
              // die Gegenseite nimmt gleich unseres an.
              await sendSignal(fromDeviceId, {
                kind: 'call',
                callId: payload.callId,
                signal: 'reject',
                video: false,
              });
              return;
            }

            // Der eigene Anruf verliert: still abraeumen und das fremde
            // Angebot annehmen, als waere nie einer ausgegangen. Kein
            // 'hangup' an die Gegenseite — sie soll ihren Anruf behalten.
            cleanUp();
          } else if (pcRef.current) {
            // Ein laufendes Gespraech: besetzt melden, statt es still zu
            // zerstoeren.
            await sendSignal(fromDeviceId, {
              kind: 'call',
              callId: payload.callId,
              signal: 'busy',
              video: false,
            });
            return;
          }

          callIdRef.current = payload.callId;
          peerRef.current = fromDeviceId;
          ringingRef.current = [fromDeviceId];

          const pc = await buildConnection(payload.callId, payload.video);
          await pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
          await drainIce(pc, pendingIce.current);

          setInfo({
            state: 'ringing',
            peerDeviceId: fromDeviceId,
            peerName,
            video: payload.video,
            incoming: true,
            error: null,
            cameraOff: false,
            minimised: false,
          });
          return;
        }

        case 'answer': {
          const pc = pcRef.current;
          if (!pc || payload.callId !== callIdRef.current) return;

          // Hat bereits ein Gerät angenommen, ist dieses hier zu spät. Ein
          // zweites setRemoteDescription würde die bestehende Verbindung
          // zerstören — also ablehnen statt annehmen.
          if (peerRef.current && peerRef.current !== fromDeviceId) {
            void sendSignal(fromDeviceId, {
              kind: 'call',
              callId: payload.callId,
              signal: 'hangup',
              video: false,
            });
            return;
          }

          peerRef.current = fromDeviceId;

          // Die übrigen Geräte der Gegenstelle hören auf zu klingeln.
          const others = ringingRef.current.filter((id) => id !== fromDeviceId);
          ringingRef.current = [fromDeviceId];
          for (const other of others) {
            void sendSignal(other, {
              kind: 'call',
              callId: payload.callId,
              signal: 'hangup',
              video: false,
            }).catch(() => {
              // Ein Gerät, das nicht erreichbar ist, klingelt ohnehin nicht.
            });
          }

          await pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
          await drainIce(pc, pendingIce.current);
          setInfo((prev) => ({ ...prev, state: 'connecting', peerDeviceId: fromDeviceId }));
          return;
        }

        case 'ice': {
          if (!payload.candidate) return;
          const pc = pcRef.current;
          if (!pc || !pc.remoteDescription) {
            pendingIce.current.push(payload.candidate);
            return;
          }
          await pc.addIceCandidate(payload.candidate).catch(() => {
            // Einzelne unbrauchbare Kandidaten sind normal.
          });
          return;
        }

        case 'hangup':
        case 'reject':
        case 'busy': {
          if (payload.callId !== callIdRef.current) return;
          // Über endWith, damit es genau EINEN Rückweg nach 'idle' gibt und
          // genau einen Timer, der ihn auslöst.
          endWith(
            payload.signal === 'busy'
              ? t('callBusy')
              : payload.signal === 'reject'
                ? t('callRejected')
                : null,
          );
        }
      }
    },
    [buildConnection, sendSignal, endWith],
  );

  /**
   * Kamera an- oder ausschalten, ohne den Anruf anzufassen.
   *
   * Die Spur wird nur stillgelegt, nicht entfernt: Ein `removeTrack` zoege
   * eine neue Aushandlung nach sich und liesse das Gespraech kurz
   * einfrieren. `enabled = false` sendet stattdessen schwarze Bilder — die
   * Gegenseite sieht Schwarz, der Kanal bleibt unberuehrt.
   */
  /**
   * Anruffenster einklappen oder wieder aufziehen.
   *
   * Der Anruf laeuft dabei weiter — nur die Anzeige tritt zurueck, damit man
   * waehrenddessen etwas nachlesen kann. Auflegen ist ein anderer Knopf; die
   * beiden zu vermischen waere der Fehler, der das Gespraech kostet.
   */
  const setMinimised = useCallback((value: boolean) => {
    setInfo((prev) => ({ ...prev, minimised: value }));
  }, []);

  const toggleCamera = useCallback(() => {
    const track = localRef.current?.getVideoTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    const off = !track.enabled;
    setInfo((prev) => ({ ...prev, cameraOff: off }));
    return off;
  }, []);

  const toggleMute = useCallback(() => {
    const track = localRef.current?.getAudioTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    return !track.enabled;
  }, []);

  return {
    info,
    localStream,
    remoteStream,
    start,
    accept,
    hangUp,
    handleSignal,
    toggleMute,
    toggleCamera,
    switchCamera,
    setMinimised,
  };
}

async function drainIce(pc: RTCPeerConnection, queue: RTCIceCandidateInit[]) {
  while (queue.length > 0) {
    const candidate = queue.shift()!;
    await pc.addIceCandidate(candidate).catch(() => {});
  }
}

function describeMediaError(err: unknown, t: (key: Key) => string): string {
  const name = (err as { name?: string }).name;
  if (name === 'NotAllowedError') return t('errPermissionDenied');
  if (name === 'NotFoundError' || name === 'NotReadableError') return t('errNoCamera');
  return `${t('errCallFailed')}: ${(err as Error).message}`;
}
